import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { OwnerFilter } from './MonitorOwnership';

// IP Reputation: the user's own IPs/domains checked against DNS blacklists.
// The API enforces ownership; buttons follow the flags it returns
// (can_check, delete_action).

const IconPlus = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
       strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);
const IconTrash = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/>
    <path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
  </svg>
);
const IconRefresh = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
    <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
  </svg>
);
const IconShield = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/>
  </svg>
);
const IconChevron = ({ open }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
       strokeLinecap="round" strokeLinejoin="round" width="14" height="14"
       style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s ease' }}>
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);

const TIER_ORDER = { critical: 0, secondary: 1, info: 2 };

const LIST_STATUS = {
  clean:   { label: 'Clean',   cls: 'status-up',      color: 'var(--success)' },
  listed:  { label: 'Listed',  cls: 'status-down',    color: 'var(--danger)' },
  unknown: { label: 'Unknown', cls: 'status-neutral', color: 'var(--text-muted)' },
};

function relTime(dateStr) {
  if (!dateStr) return 'never';
  const mins = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return mins + 'm ago';
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return hrs + 'h ago';
  return Math.floor(hrs / 24) + 'd ago';
}

function OverallBadge({ overall }) {
  if (overall.status === 'listed') {
    const parts = [];
    if (overall.listed_critical) parts.push(overall.listed_critical + ' critical');
    if (overall.listed_secondary) parts.push(overall.listed_secondary + ' secondary');
    return <span className="status-badge status-down"><span className="badge-dot" />Listed on {parts.join(' + ')}</span>;
  }
  if (overall.status === 'clean') {
    return <span className="status-badge status-up"><span className="badge-dot" />Clean</span>;
  }
  if (overall.status === 'unknown') {
    return (
      <span className="status-badge status-neutral" title={overall.unknown_lists + ' list(s) could not be checked'}>
        <span className="badge-dot" />Unknown ({overall.unknown_lists})
      </span>
    );
  }
  return <span className="status-badge status-neutral"><span className="badge-dot" />Pending</span>;
}

// Per-list grid for one target.
function ListGrid({ target, catalog }) {
  const byId = {};
  target.results.forEach(r => { byId[r.list_id] = r; });
  const lists = catalog
    .filter(l => l.kind === target.kind)
    .sort((a, b) => TIER_ORDER[a.tier] - TIER_ORDER[b.tier] || a.name.localeCompare(b.name));

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 8, padding: '12px 4px' }}>
      {lists.map(l => {
        const r = byId[l.id];
        const meta = LIST_STATUS[r ? r.status : 'unknown'];
        return (
          <div key={l.id} style={{ border: '1px solid var(--border)', borderLeft: '3px solid ' + meta.color,
                                   borderRadius: 'var(--radius-sm)', padding: '8px 10px', minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
              <span style={{ fontWeight: 600, fontSize: 12.5, color: 'var(--text-primary)' }}>{l.name}</span>
              <span className={'status-badge ' + meta.cls}>{r ? meta.label : 'Not checked'}</span>
            </div>
            <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginTop: 3, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {l.tier}{l.tier === 'info' ? ' · never emailed' : ''}
            </div>
            {r && r.pending_status && (
              <div style={{ fontSize: 11, color: 'var(--warning)', marginTop: 4 }}>
                Change to {r.pending_status} seen — confirming at {new Date(r.pending_due_at).toLocaleTimeString()}
              </div>
            )}
            {r && r.reason && (
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4, overflowWrap: 'anywhere' }} title={r.reason}>
                {r.reason.length > 160 ? r.reason.slice(0, 160) + '…' : r.reason}
              </div>
            )}
            {r && r.status === 'listed' && (
              <a href={l.delist_url} target="_blank" rel="noopener noreferrer"
                 style={{ display: 'inline-block', fontSize: 11.5, marginTop: 5, color: 'var(--primary)', fontWeight: 600 }}>
                Request delisting ↗
              </a>
            )}
          </div>
        );
      })}
    </div>
  );
}

function IPReputation({ user }) {
  const [targets, setTargets]         = useState([]);
  const [catalog, setCatalog]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [expanded, setExpanded]       = useState({});
  const [ownerFilter, setOwnerFilter] = useState(''); // admin only: '' | '<userId>'
  const [showAdd, setShowAdd]         = useState(false);
  const [form, setForm]               = useState({ target: '', label: '' });
  const [error, setError]             = useState('');
  const [busy, setBusy]               = useState(false);
  const [checkingId, setCheckingId]   = useState(null);
  const [checkingAll, setCheckingAll] = useState(false);
  const [notice, setNotice]           = useState('');
  const [suggest, setSuggest]         = useState(null); // { ips, ranges } while the modal is open
  const [addingIp, setAddingIp]       = useState(null);

  const canEdit = user?.role === 'admin' || user?.role === 'editor';
  const viewingOtherUser = ownerFilter !== '';

  const fetchTargets = useCallback(async () => {
    try {
      const res = await api.get('/reputation', { params: ownerFilter ? { owner: ownerFilter } : {} });
      setTargets(res.data);
    } catch (err) {
      console.error('Failed to fetch reputation targets:', err);
    } finally {
      setLoading(false);
    }
  }, [ownerFilter]);

  useEffect(() => {
    api.get('/reputation/lists').then(res => setCatalog(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    fetchTargets();
    const interval = setInterval(fetchTargets, 60000);
    return () => clearInterval(interval);
  }, [fetchTargets]);

  const apiError = (err, fallback) => err.response?.data?.error || fallback;

  const handleAdd = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await api.post('/reputation', form);
      setForm({ target: '', label: '' });
      setShowAdd(false);
      fetchTargets();
    } catch (err) {
      setError(apiError(err, 'Failed to add target'));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (t) => {
    if (!window.confirm('Delete "' + t.value + '" and its check history?')) return;
    try {
      await api.delete('/reputation/' + t.id);
      fetchTargets();
    } catch (err) {
      alert(apiError(err, 'Failed to delete target'));
    }
  };

  const handleCheck = async (t) => {
    setCheckingId(t.id);
    setNotice('');
    try {
      await api.post('/reputation/' + t.id + '/check');
      await fetchTargets();
    } catch (err) {
      alert(apiError(err, 'Check failed'));
    } finally {
      setCheckingId(null);
    }
  };

  const handleCheckAll = async () => {
    setCheckingAll(true);
    setNotice('');
    try {
      const res = await api.post('/reputation/check-all');
      setNotice('Checked ' + res.data.checked + ' target(s)' + (res.data.skipped ? ', ' + res.data.skipped + ' already in progress' : '') +
                '. Changes are confirmed by a second check a few minutes later before any email is sent.');
      await fetchTargets();
    } catch (err) {
      setNotice(apiError(err, 'Check all failed'));
    } finally {
      setCheckingAll(false);
    }
  };

  const openSuggest = async () => {
    try {
      const res = await api.get('/reputation/suggestions');
      setSuggest(res.data);
    } catch (err) {
      alert(apiError(err, 'Could not load SPF suggestions'));
    }
  };

  const addSuggested = async (ip) => {
    setAddingIp(ip.value);
    try {
      await api.post('/reputation', { target: ip.value, label: 'SPF: ' + ip.domains.slice(0, 2).join(', ') });
      setSuggest(s => ({ ...s, ips: s.ips.map(x => x.value === ip.value ? { ...x, already_added: true } : x) }));
      fetchTargets();
    } catch (err) {
      alert(apiError(err, 'Failed to add ' + ip.value));
    } finally {
      setAddingIp(null);
    }
  };

  const counts = { clean: 0, listed: 0, unknown: 0 };
  targets.forEach(t => {
    if (t.overall.status === 'pending') counts.unknown++;
    else counts[t.overall.status]++;
  });

  if (loading) {
    return <div className="loading"><div className="spinner" /><span>Loading IP reputation…</span></div>;
  }

  return (
    <div>
      <div className="page-header">
        <div className="page-header-top">
          <div>
            <h1>IP Reputation</h1>
            <p>Your IPs and domains checked against {catalog.length || 'the'} DNS blacklists</p>
          </div>
          {canEdit && !viewingOtherUser && (
            <div className="page-actions">
              <button className="btn btn-secondary" onClick={handleCheckAll} disabled={checkingAll || targets.length === 0}>
                <IconRefresh /> {checkingAll ? 'Checking…' : 'Check all'}
              </button>
              <button className="btn btn-secondary" onClick={openSuggest}>Suggest from SPF</button>
              <button className="btn btn-primary" onClick={() => { setShowAdd(true); setError(''); }}>
                <IconPlus /> Add target
              </button>
            </div>
          )}
        </div>
      </div>

      {notice && <div className="alert alert-success" style={{ marginBottom: 16 }}>{notice}</div>}

      {targets.length > 0 && (
        <div className="cards-grid" style={{ marginBottom: 20, gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}>
          {[
            { label: 'Targets', val: targets.length, color: 'var(--info)' },
            { label: 'Clean',   val: counts.clean,   color: 'var(--success)' },
            { label: 'Listed',  val: counts.listed,  color: 'var(--danger)' },
            { label: 'Unknown', val: counts.unknown, color: 'var(--text-muted)' },
          ].map(s => (
            <div key={s.label} className="card">
              <div className="card-title" style={{ marginBottom: 8 }}>{s.label}</div>
              <div className="card-value" style={{ fontSize: 26, color: s.val > 0 ? s.color : 'var(--text-muted)' }}>{s.val}</div>
            </div>
          ))}
        </div>
      )}

      <div className="table-container">
        <div className="table-header">
          <h3>Targets</h3>
          <div className="table-toolbar">
            <OwnerFilter user={user} value={ownerFilter} onChange={setOwnerFilter} showDefaults={false} />
          </div>
        </div>

        {targets.length === 0 ? (
          <div className="table-empty">
            <div className="table-empty-icon"><IconShield /></div>
            <div className="table-empty-title">{viewingOtherUser ? 'This user has no targets' : 'No targets yet'}</div>
            <div className="table-empty-desc">
              {canEdit && !viewingOtherUser ? 'Add your mail server IPs or domains, or use "Suggest from SPF"' : 'Nothing to show'}
            </div>
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th style={{ width: 28 }}></th>
                <th>Target</th>
                <th>Label</th>
                <th>Status</th>
                <th>Last checked</th>
                {canEdit && <th style={{ width: 150 }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {targets.map(t => (
                <React.Fragment key={t.id}>
                  <tr style={{ cursor: 'pointer' }} onClick={() => setExpanded(x => ({ ...x, [t.id]: !x[t.id] }))}>
                    <td style={{ color: 'var(--text-muted)' }}><IconChevron open={!!expanded[t.id]} /></td>
                    <td>
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{t.value}</span>
                      <span className="status-badge status-neutral owner-badge" style={{ marginLeft: 8 }}>{t.kind === 'ip' ? 'IPv4' : 'Domain'}</span>
                    </td>
                    <td style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{t.label || <span style={{ color: 'var(--text-muted)' }}>-</span>}</td>
                    <td>
                      <OverallBadge overall={t.overall} />
                      {t.overall.pending_changes > 0 && (
                        <div style={{ fontSize: 10.5, color: 'var(--warning)', marginTop: 3 }}>
                          {t.overall.pending_changes} change(s) awaiting confirmation
                        </div>
                      )}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{relTime(t.last_checked_at)}</td>
                    {canEdit && (
                      <td onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {t.can_check && (
                            <button className="btn btn-secondary btn-sm" disabled={checkingId === t.id} onClick={() => handleCheck(t)}>
                              <IconRefresh /> {checkingId === t.id ? 'Checking…' : 'Check now'}
                            </button>
                          )}
                          {t.delete_action === 'delete' && (
                            <button className="btn btn-danger btn-sm" title="Delete" onClick={() => handleDelete(t)}>
                              <IconTrash />
                            </button>
                          )}
                          {!t.can_check && !t.delete_action && (
                            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Read-only</span>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                  {expanded[t.id] && (
                    <tr>
                      <td colSpan={canEdit ? 6 : 5} style={{ background: 'var(--bg-table-head)' }}>
                        <ListGrid target={t} catalog={catalog} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showAdd && (
        <div className="modal-overlay" onClick={() => setShowAdd(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add target</h3>
              <button className="modal-close" onClick={() => setShowAdd(false)}>×</button>
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-error">{error}</div>}
              <div className="form-hint" style={{ marginBottom: 12 }}>
                Targets are private: only you see them and get their alerts.
              </div>
              <form onSubmit={handleAdd}>
                <div className="form-group">
                  <label>IPv4 address or domain <span className="label-hint">*required</span></label>
                  <input type="text" value={form.target} autoFocus required placeholder="203.0.113.10 or example.com"
                         onChange={e => setForm({ ...form, target: e.target.value })} />
                  <div className="form-hint">Public IPv4 addresses are checked on IP lists, domains on domain lists. IPv6 is not supported yet.</div>
                </div>
                <div className="form-group">
                  <label>Label <span className="label-hint">optional</span></label>
                  <input type="text" value={form.label} placeholder="e.g. Outbound mail relay"
                         onChange={e => setForm({ ...form, label: e.target.value })} />
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowAdd(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Adding & checking…' : 'Add & check'}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {suggest && (
        <div className="modal-overlay" onClick={() => setSuggest(null)}>
          <div className="modal" style={{ maxWidth: 600 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Suggestions from SPF records</h3>
              <button className="modal-close" onClick={() => setSuggest(null)}>×</button>
            </div>
            <div className="modal-body">
              {suggest.ips.length === 0 && suggest.ranges.length === 0 ? (
                <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                  No ip4: entries found in the Email Records SPF data you can access.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 420, overflowY: 'auto' }}>
                  {suggest.ips.map(ip => (
                    <div key={ip.value} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                                                 padding: '7px 12px', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--text-primary)' }}>{ip.value}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', overflowWrap: 'anywhere' }}>{ip.from} · {ip.domains.join(', ')}</div>
                      </div>
                      <button className="btn btn-secondary btn-sm" disabled={ip.already_added || addingIp === ip.value}
                              onClick={() => addSuggested(ip)}>
                        {ip.already_added ? 'Added' : addingIp === ip.value ? 'Adding…' : 'Add'}
                      </button>
                    </div>
                  ))}
                  {suggest.ranges.map(r => (
                    <div key={r.cidr} style={{ padding: '7px 12px', border: '1px dashed var(--border)', borderRadius: 'var(--radius-sm)' }}>
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--text-primary)' }}>
                        {r.cidr} <span style={{ fontFamily: 'var(--font)', fontSize: 11, color: 'var(--warning)' }}>{r.note}</span>
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', overflowWrap: 'anywhere' }}>{r.domains.join(', ')}</div>
                    </div>
                  ))}
                </div>
              )}
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setSuggest(null)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default IPReputation;
